import { useEffect, useMemo, useState } from 'react';
import { rankingList, type RankingItem } from '@/mocks/chaoyang';

const filters = ['全部', '明星', '综艺', '影视', '体育', '网红', '时尚', '音乐', '粉丝', '电商'];

const trendIcon: Record<RankingItem['trend'], string> = {
  up: 'ri-arrow-up-line',
  down: 'ri-arrow-down-line',
  flat: 'ri-drag-move-line',
  new: 'ri-sparkling-line',
};

export default function RankingSection() {
  const [active, setActive] = useState('全部');
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    const onSearch = (e: Event) => {
      const detail = (e as CustomEvent).detail as { keyword: string };
      if (detail?.keyword) setKeyword(detail.keyword);
    };
    window.addEventListener('chigua-search', onSearch);
    return () => window.removeEventListener('chigua-search', onSearch);
  }, []);

  const list = useMemo(() => {
    return rankingList.filter((item) => {
      const matchCat = active === '全部' || item.category === active;
      const matchKw = keyword ? item.title.includes(keyword) || item.category.includes(keyword) : true;
      return matchCat && matchKw;
    });
  }, [active, keyword]);

  const clear = () => setKeyword('');

  return (
    <section id="bang" className="mx-auto w-full max-w-7xl scroll-mt-20 px-4 py-12 md:px-6 md:py-16">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="mb-2 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600">
            <i className="ri-water-flash-line"></i> 今日聚焦
          </p>
          <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">朝阳吃瓜榜</h2>
          <p className="mt-2 text-sm text-foreground-600">
            实时聚合全网讨论热度的实时排序，一眼看懂今天最受关注的朝阳吃瓜热门话题。
          </p>
        </div>
        <div id="search-area" className="flex w-full items-center rounded-full border border-background-300 bg-background-50 px-4 py-2.5 sm:w-72">
          <i className="ri-search-line text-foreground-500"></i>
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="筛选吃瓜榜话题"
            className="w-full bg-transparent px-2 text-sm outline-none placeholder:text-foreground-400"
          />
          {keyword && (
            <button onClick={clear} aria-label="清除" className="text-foreground-400 hover:text-foreground-700">
              <i className="ri-close-circle-line"></i>
            </button>
          )}
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        {filters.map((f) => (
          <button
            key={f}
            onClick={() => setActive(f)}
            className={`rounded-full px-3 py-1.5 text-sm transition ${
              active === f
                ? 'bg-primary-500 text-background-50'
                : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-background-300 bg-background-50 p-12 text-center">
          <i className="ri-emotion-sad-line text-3xl text-foreground-300"></i>
          <p className="mt-3 text-sm text-foreground-600">没有匹配“{keyword}”的吃瓜榜话题，换个关键词试试。</p>
          <button
            onClick={clear}
            className="mt-4 rounded-full bg-primary-500 px-4 py-2 text-sm text-background-50 hover:bg-primary-600"
          >
            查看全部
          </button>
        </div>
      ) : (
        <ol className="grid gap-3 md:grid-cols-2">
          {list.map((item) => (
            <li
              key={item.rank}
              className="group flex items-center gap-4 rounded-xl border border-background-200 bg-background-50 p-4 transition hover:border-primary-200 hover:bg-primary-50/40"
            >
              <span
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg font-heading text-lg font-bold ${
                  item.rank <= 3 ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-600'
                }`}
              >
                {item.rank}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="rounded-md bg-secondary-100 px-1.5 py-0.5 text-xs text-secondary-900">{item.category}</span>
                  {item.isTop && (
                    <span className="text-xs font-semibold text-primary-600">
                      <i className="ri-fire-fill mr-0.5"></i>爆
                    </span>
                  )}
                </div>
                <h3 className="mt-1 truncate text-sm font-medium text-foreground-950 group-hover:text-primary-700 md:text-base">
                  {item.title}
                </h3>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1">
                <span className="flex items-center gap-1 text-xs text-primary-600">
                  <i className={trendIcon[item.trend]}></i>
                  {item.heat}
                </span>
                <span className="text-[11px] text-foreground-400">热度值</span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}